import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useActionRequired } from '../hooks/useActionRequired';
import { OrderRow } from '../components/OrderRow';

export function ActionRequired() {
  const [hasAiInProgress, setHasAiInProgress] = useState(false);
  const { data: groups, isLoading, error } = useActionRequired(hasAiInProgress);
  const [search, setSearch] = useState('');

  // Track AI in-progress state from the data
  const aiInProgress = groups?.some((g) =>
    g.orders.some(
      (o) => o.aiStatus === 'started' || o.aiStatus === 'fetching-context' || o.aiStatus === 'analyzing'
    )
  ) ?? false;
  if (aiInProgress !== hasAiInProgress) {
    setHasAiInProgress(aiInProgress);
  }

  const totalActionRequired = groups?.reduce((sum, g) => sum + g.orders.length, 0) ?? 0;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">
          Error loading action-required orders:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Open Issues</h1>
          <p className="text-sm text-gray-500 mt-1">
            Orders that need attention across all batches
          </p>
        </div>
        {!isLoading && (
          <div className="text-right">
            <p className="text-3xl font-semibold text-red-600">{totalActionRequired}</p>
            <p className="text-sm text-gray-500">orders need action</p>
          </div>
        )}
      </div>

      {/* Search */}
      <div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search orders by ID, reason, or notes..."
          className="w-full sm:w-80 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
              <div className="p-4 border-b border-gray-200">
                <div className="h-5 bg-gray-200 rounded w-48" />
              </div>
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="flex space-x-4">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && totalActionRequired === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">All clear</h3>
          <p className="mt-1 text-sm text-gray-500">No action-required orders at this time.</p>
        </div>
      )}

      {/* Date groups */}
      {groups?.map((group) => {
        const filteredOrders = search
          ? group.orders.filter((o) => {
              const s = search.toLowerCase();
              return (
                o.orderId.toLowerCase().includes(s) ||
                (o.reason && o.reason.toLowerCase().includes(s)) ||
                (o.notes && o.notes.toLowerCase().includes(s))
              );
            })
          : group.orders;

        if (filteredOrders.length === 0) return null;

        const formattedDate = new Date(group.date + 'T00:00:00').toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });

        return (
          <div key={group.date} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Date group header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <Link
                  to={`/batch/${group.date}`}
                  className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                >
                  {formattedDate}
                </Link>
                {group.assignedTo && (
                  <p className="text-sm text-gray-500">Assigned to {group.assignedTo}</p>
                )}
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {filteredOrders.length} need action
              </span>
            </div>

            {/* Orders table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">&nbsp;</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <OrderRow key={`${group.date}-${order.orderId}`} order={order} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
